import { useState } from "react"
import { Head } from "@inertiajs/react"

import AuthAlert from "@/components/tuw/auth/AuthAlert"
import { CsrfField } from "@/components/tuw/auth/fields"
import ResendButton from "@/components/tuw/auth/ResendButton"
import CodeInput, { CODE_LENGTH } from "@/components/tuw/onboarding/CodeInput"
import OnboardingLayout, { StepTitle, stepKicker } from "@/components/tuw/onboarding/OnboardingLayout"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { maskEmail } from "@/lib/maskEmail"

// Step one of onboarding (P11-H4 Four Digits): the code from the sign-up
// email, posted as `customer[authentication_code]` to the same action as
// before. The resend row is its own form (a form cannot nest), so the verify
// button is tied to the verify form by id. After a resend the server sets
// `resent` and the button holds a 30 second cooldown.
export default function Verify({
  email,
  rail,
  initials,
  signOut,
  action,
  resendAction,
  wrongCode = false,
  resent = false,
  flash = {},
}) {
  const [code, setCode] = useState("")

  return (
    <OnboardingLayout step={1} rail={rail} email={email} initials={initials} signOut={signOut} narrow>
      <Head title="Verify your email · The Urban Writers" />

      <StepTitle kicker={stepKicker(1, rail)} title="Verify your email">
        We sent a 4-digit code to <b className="font-display">{maskEmail(email)}</b>. Enter it below to confirm this
        is you.
      </StepTitle>

      {wrongCode && (
        <AuthAlert tone="bad">
          <b>That code isn’t right.</b> Check the most recent email from us and try again.
        </AuthAlert>
      )}
      {resent && (
        <AuthAlert tone="good">
          <b>New code sent.</b> Only the newest code works.
        </AuthAlert>
      )}
      {flash.alert && <AuthAlert tone="bad">{flash.alert}</AuthAlert>}

      <form id="verify-form" method="post" action={action} className="flex flex-col gap-[7px]">
        <CsrfField />
        <Label className="text-[12.5px] font-bold">Authentication code</Label>
        <CodeInput name="customer[authentication_code]" invalid={wrongCode} onChange={setCode} />
      </form>

      <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-muted-foreground">
        <span>Didn’t get it?</span>
        <form method="post" action={resendAction}>
          <CsrfField />
          <ResendButton cooldown={resent ? 30 : 0} label="Resend code" />
        </form>
      </div>

      <AuthAlert tone="quiet">
        Not there after a minute? Check your <b>spam or junk</b> folder. Still nothing after 10 minutes? Email{" "}
        <a href="mailto:support@dibbly.com">support@dibbly.com</a>.
      </AuthAlert>

      <div className="flex justify-end pt-1.5 max-[560px]:justify-stretch">
        <Button
          type="submit"
          form="verify-form"
          disabled={code.length < CODE_LENGTH}
          className="h-auto rounded-[11px] px-5 py-3 text-[14px] font-semibold max-[560px]:w-full"
        >
          Verify and continue
        </Button>
      </div>
    </OnboardingLayout>
  )
}
